import React, { useState, useEffect, useContext, useRef } from "react";
import "./baseScheduleModal.css";
import translations from "../../../translations/translations";
import { CombinedContext } from "../../../context/combinedContext";
import scheduleBasedRequest from "../../../request/scheduleBasedRequest";
import { Trash2 } from "lucide-react";
import StaffDropdown from "../../common/StaffDropdown/StaffDropdown";

const defaultDays = [
	{ dayEN: "Monday", dayES: "Lunes", start: "09:00", end: "17:00", open: true, breaks: [] },
	{ dayEN: "Tuesday", dayES: "Martes", start: "09:00", end: "17:00", open: true, breaks: [] },
	{ dayEN: "Wednesday", dayES: "Miércoles", start: "09:00", end: "17:00", open: true, breaks: [] },
	{ dayEN: "Thursday", dayES: "Jueves", start: "09:00", end: "17:00", open: true, breaks: [] },
	{ dayEN: "Friday", dayES: "Viernes", start: "09:00", end: "17:00", open: true, breaks: [] },
	{ dayEN: "Saturday", dayES: "Sábado", start: "09:00", end: "17:00", open: false, breaks: [] },
	{ dayEN: "Sunday", dayES: "Domingo", start: "09:00", end: "17:00", open: false, breaks: [] },
];

const BaseScheduleModal = ({ onClose, staffList, initialStaffId, token }) => {
	const { language, userData, setUserData } = useContext(CombinedContext);
	// const scheduleId = userData?.user?.scheduleId;

	const role = userData?.user?.role;

	const [days, setDays] = useState(defaultDays);
	const [expandedDay, setExpandedDay] = useState(null);
	const [saving, setSaving] = useState(false);
	const [selectedStaffId, setSelectedStaffId] = useState(null);
	const [selectedScheduleId, setSelectedScheduleId] = useState(null);
	const [currentSchedule, setCurrentSchedule] = useState(null);
	const [loadingSchedule, setLoadingSchedule] = useState(false);
	const [staffDropdownOpen, setStaffDropdownOpen] = useState(false);

	const hasInitialized = useRef(false);

	useEffect(() => {
		if (staffList.length > 0 && !hasInitialized.current) {
			hasInitialized.current = true;
			const target = initialStaffId ? staffList.find((s) => s.staffId === initialStaffId) : staffList[0];
			const first = target || staffList[0];
			setSelectedStaffId(first.staffId);
			setSelectedScheduleId(first.scheduleId);
			loadSchedule(first);
		}
	}, [staffList]);

	useEffect(() => {
		if (!currentSchedule?.days?.length) return;

		const loaded = defaultDays.map((d) => {
			const found = currentSchedule.days.find((x) => x.dayEN === d.dayEN);
			return found
				? {
						dayEN: found.dayEN,
						dayES: found.dayES,
						open: true,
						start: found.shifts?.[0]?.start || d.start,
						end: found.shifts?.[0]?.end || d.end,
						breaks: found.breaks || [],
					}
				: d;
		});

		setDays(loaded);
	}, [currentSchedule]);

	const timeStringToMinutes = (time) => {
		if (!time) return null;
		const [h, m] = time.split(":").map(Number);
		return h * 60 + m;
	};

	const times = Array.from({ length: 48 }, (_, i) => {
		const h = Math.floor(i / 2);
		const m = i % 2 === 0 ? "00" : "30";
		return `${String(h).padStart(2, "0")}:${m}`;
	});

	const toggleOpen = (index) => {
		const updated = [...days];
		updated[index].open = !updated[index].open;

		// auto-collapse if closing the day
		if (!updated[index].open && expandedDay === updated[index].dayEN) {
			setExpandedDay(null);
		}

		setDays(updated);
	};

	const handleTimeChange = (i, field, value) => {
		const updated = [...days];
		updated[i][field] = value;
		setDays(updated);
	};

	const addBreak = (i) => {
		const updated = [...days];
		updated[i].breaks.push({ start: "12:00", end: "13:00" });
		setDays(updated);
	};

	const handleBreakChange = (i, bi, field, value) => {
		setDays((prev) => {
			const updated = [...prev];
			const day = { ...updated[i] };
			const breaks = [...day.breaks];
			const brk = { ...breaks[bi] };

			const shiftStart = timeStringToMinutes(day.start);
			const shiftEnd = timeStringToMinutes(day.end);

			const newStart = field === "start" ? value : brk.start;
			const newEnd = field === "end" ? value : brk.end;

			const newStartMin = timeStringToMinutes(newStart);
			const newEndMin = timeStringToMinutes(newEnd);

			// ❌ must be inside shift
			if (newStartMin <= shiftStart || newEndMin >= shiftEnd) {
				return prev;
			}

			// ❌ break must be start < end
			if (newStartMin >= newEndMin) {
				return prev;
			}

			// ✅ accept change
			brk[field] = value;
			breaks[bi] = brk;
			day.breaks = breaks;
			updated[i] = day;

			return updated;
		});
	};

	const removeBreak = (i, bi) => {
		const updated = [...days];
		updated[i].breaks.splice(bi, 1);
		setDays(updated);
	};

	const toggleExpand = (dayEN, open) => {
		if (!open) return; // cannot expand closed days
		setExpandedDay(expandedDay === dayEN ? null : dayEN);
	};

	const handleSave = async () => {
		if (!selectedScheduleId) return;

		const payload = {
			days: days
				.filter((d) => d.open)
				.map((d) => ({
					dayEN: d.dayEN,
					dayES: d.dayES,
					shifts: [{ start: d.start, end: d.end }],
					breaks: d.breaks.map((b) => ({ start: b.start, end: b.end })),
				})),
		};

		try {
			setSaving(true);
			const res = await scheduleBasedRequest.updateSchedule(selectedScheduleId, payload, userData.cognitoTokens.AccessToken);

			if (role === "admin" || role === "owner") {
				setUserData((prev) => ({ ...prev, shop: res.shop }));
			}

			if (role === "staff") {
				setUserData((prev) => {
					const onboarding = prev?.user?.onboarding || {};
					const currentSteps = onboarding.steps;

					// 🚫 If no steps → do NOTHING at all
					if (!currentSteps) return prev;

					// Only update if steps exist
					const steps = { ...currentSteps };

					if ("schedule" in steps) {
						steps.schedule = true;
					}

					const required = onboarding.requiredSteps || [];
					const isComplete = required.every((step) => steps[step]);

					return {
						...prev,
						user: {
							...prev.user,
							onboarding: {
								...onboarding,
								steps, // only included because it already existed
								isComplete,
								lastUpdatedAt: new Date().toISOString(),
							},
						},
					};
				});
			}

			onClose();
		} catch (err) {
			console.error("Error saving schedule:", err);
		} finally {
			setSaving(false);
		}
	};

	const loadSchedule = (staff) => {
		if (!staff?.week) return;

		const mappedDays = defaultDays.map((templateDay) => {
			// find matching day in staff.week by dayEN → Mon, Tue...
			const short = templateDay.dayEN.slice(0, 3); // "Mon"
			const weekEntry = Object.values(staff.week).find((d) => d.dayName.slice(0, 3).toLowerCase() === short.toLowerCase());

			if (!weekEntry || !weekEntry.shifts) {
				return {
					...templateDay,
					open: false,
					breaks: [],
				};
			}

			const firstShift = weekEntry.shifts[0];

			return {
				...templateDay,
				open: weekEntry.shifts.length > 0,
				start: firstShift?.start || templateDay.start,
				end: firstShift?.end || templateDay.end,
				breaks: weekEntry.breaks || [],
			};
		});

		setDays(mappedDays);
	};

	function formatTimeDisplay(time) {
		if (!time) return "";

		const [h, m] = time.split(":").map(Number);
		const hour12 = h % 12 || 12;
		const minutes = String(m).padStart(2, "0");
		const period = h >= 12 ? "PM" : "AM";

		return `${hour12}:${minutes} ${period}`;
	}

	return (
		<div className="sf-base-overlay" onClick={onClose}>
			<div className="sf-base-modal" onClick={(e) => e.stopPropagation()}>
				{/* HEADER */}
				<div className="sf-base-header">
					<h2>{translations[language].calendar.editor.title}</h2>
					<button className="sf-base-close" onClick={onClose}>
						✕
					</button>
				</div>

				<div className="sf-sdd-div">
					{staffList.length > 1 && (
						<StaffDropdown
							staffList={staffList}
							selectedStaffId={selectedStaffId || ""}
							onSelect={(id) => {
								const found = staffList.find((s) => s.staffId === id);
								if (found) {
									setSelectedStaffId(found.staffId);
									setSelectedScheduleId(found.scheduleId);
									loadSchedule(found);
								}
								setStaffDropdownOpen(false);
							}}
							language={language}
							open={staffDropdownOpen}
							onToggle={() => setStaffDropdownOpen((p) => !p)}
							showAnyOption={false}
						/>
					)}
				</div>

				{/* BODY */}
				<div className="sf-base-body">
					{days.map((d, i) => {
						const dayLabel = language === "es" ? d.dayES : d.dayEN;
						const expanded = expandedDay === d.dayEN;

						return (
							<div key={i} className="sf-week-item">
								{/* ROW HEADER */}
								<div className="sf-week-header" onClick={() => toggleExpand(d.dayEN, d.open)}>
									<input
										type="checkbox"
										className="sf-toggle"
										checked={d.open}
										onChange={() => toggleOpen(i)}
										onClick={(e) => e.stopPropagation()}
									/>

									<span className="sf-week-day">{dayLabel}</span>

									{d.open ? (
										<span className="sf-week-hours">
											{formatTimeDisplay(d.start)} – {formatTimeDisplay(d.end)}
											{d.breaks.length > 0 && (
												<span className="sf-break-count">
													({d.breaks.length} {translations[language].calendar.break}
													{d.breaks.length > 1 ? "s" : ""})
												</span>
											)}
										</span>
									) : (
										<span className="sf-week-closed">{translations[language].calendar.editor.closed}</span>
									)}

									<span className={`sf-chevron ${expanded ? "open" : ""}`}>⌄</span>
								</div>

								{/* EXPANDED CARD */}
								{expanded && d.open && (
									<div className="sf-day-card">
										{/* HOURS */}
										<div className="sf-section">
											<div className="sf-section-label">{translations[language].calendar.editor.hours}</div>
											<div className="sf-time-row">
												<select value={d.start} onChange={(e) => handleTimeChange(i, "start", e.target.value)}>
													{times.map((t) => (
														<option key={t} value={t}>
															{formatTimeDisplay(t)}
														</option>
													))}
												</select>

												<span className="sf-dash">—</span>

												<select value={d.end} onChange={(e) => handleTimeChange(i, "end", e.target.value)}>
													{times
														.filter((t) => t > d.start)
														.map((t) => (
															<option key={t} value={t}>
																{formatTimeDisplay(t)}
															</option>
														))}
												</select>
											</div>
										</div>

										{/* BREAKS */}
										<div className="sf-section">
											<div className="sf-section-label">{translations[language].calendar.editor.breaks}</div>

											{d.breaks.map((b, bi) => (
												<div key={bi} className="sf-break-item">
													<div className="sf-break-row">
														<select value={b.start} onChange={(e) => handleBreakChange(i, bi, "start", e.target.value)}>
															{times
																.filter((t) => t > d.start && t < b.end)
																.map((t) => (
																	<option key={t} value={t}>
																		{formatTimeDisplay(t)}
																	</option>
																))}
														</select>

														<span className="sf-dash">—</span>

														<select value={b.end} onChange={(e) => handleBreakChange(i, bi, "end", e.target.value)}>
															{times
																.filter((t) => t > b.start && t < d.end)
																.map((t) => (
																	<option key={t} value={t}>
																		{formatTimeDisplay(t)}
																	</option>
																))}
														</select>
													</div>

													<button className="sf-break-delete" onClick={() => removeBreak(i, bi)}>
														<Trash2 size={20} strokeWidth={1.8} />
													</button>
												</div>
											))}

											<button className="sf-add-break" onClick={() => addBreak(i)}>
												{translations[language].calendar.breaks.add}
											</button>
										</div>
									</div>
								)}
							</div>
						);
					})}
				</div>

				{/* FOOTER */}
				<div className="sf-base-footer">
					<button className="btn ghost" onClick={onClose}>
						{translations[language].calendar.editor.cancel}
					</button>
					<button className="btn" onClick={handleSave} disabled={saving}>
						{saving ? translations[language].calendar.editor.saving : translations[language].calendar.editor.save}
					</button>
				</div>
			</div>
		</div>
	);
};

export default BaseScheduleModal;
